import axios from 'axios';
import { base_url } from './api-config';
import {
    AnalyticsReport,
    BestSellingProduct,
    CategorySalesSummary,
    MemberReportItem,
    ReceiptItem,
    ReportFilter,
    SalesKPISummary,
    ShiftReportItem,
    StockReportItem,
} from '../model/report';
import { formatActor, UNKNOWN_ACTOR_SHORT } from '../utils/audit-format';
import { formatDateTH } from '../utils/date-filter-utils';

/*
 * Every figure on the report screens comes from Backend `/api/v1/report`.
 *
 * Until 2026-09-08 this module never called that endpoint, although both it
 * and the Backend service existed and were shaped for each other. Instead
 * it fetched raw sale rows and invented the rest: gross profit as 38% of
 * sales, a daily trend of six hardcoded days, a manager's name for every
 * bill whose cashier was unknown, sample receipts whenever the day was
 * empty or the call failed, a hardcoded list of best-selling products,
 * product cost as 60% of price, and a stock list of five demo items.
 *
 * Backend computes all of it from `invoice`, `invoice_detail`, `product`,
 * `stock`, `work_shift` and `member`. Nothing here estimates, samples or
 * substitutes. A failed call is an error the screen shows; an empty
 * answer is an empty screen.
 */

const CONNECTION_FAILED = 'ไม่สามารถเชื่อมต่อ Backend สำหรับข้อมูลรายงานได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง';

const num = (v: any): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/** The subset of a ReportFilter that Backend understands, without empties. */
const bodyOf = (filter?: Partial<ReportFilter>): Record<string, any> => {
    const body: Record<string, any> = {};
    if (!filter) return body;
    if (filter.startDate) body.startDate = filter.startDate;
    if (filter.endDate) body.endDate = filter.endDate;
    if (filter.cashierId) body.cashierId = filter.cashierId;
    if (filter.paymentType) body.paymentType = filter.paymentType;
    if (filter.categoryId) body.categoryId = filter.categoryId;
    if (filter.searchTerm) body.searchTerm = filter.searchTerm;
    return body;
};

export const api_report = async (operation: string, body: any = {}): Promise<any> => {
    let res;
    try {
        res = await axios.post(`${base_url}/api/v1/report`, JSON.stringify(body ?? {}), {
            headers: { 'Content-Type': 'application/json', operation },
        });
    } catch (e: any) {
        // A response carries Backend's own message -- and the 401 the auth
        // interceptor needs to see. Pass it through untouched.
        if (e?.response) throw e;
        throw new Error(CONNECTION_FAILED);
    }
    if (res && res.status === 200 && res.data !== undefined && res.data !== null) {
        return res.data;
    }
    throw new Error(`Backend ส่งผลลัพธ์ ${String(operation).toUpperCase()} ที่ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง`);
};

// 1. Sales summary: KPIs, receipts (with lines), daily trend.
export const fetchSalesReport = async (filter: ReportFilter): Promise<{
    kpis: SalesKPISummary;
    receipts: ReceiptItem[];
    dailyTrend: { date: string; sales: number; bills: number }[];
}> => {
    const data = await api_report('SALES', bodyOf(filter));
    const k = data?.kpis || {};
    const kpis: SalesKPISummary = {
        totalSales: num(k.totalSales),
        totalBills: num(k.totalBills),
        avgBillAmount: num(k.avgBillAmount),
        totalDiscount: num(k.totalDiscount),
        totalVat: num(k.totalVat),
        totalItemsSold: num(k.totalItemsSold),
        // null from Backend means "no invoice lines to compute from" --
        // rendered as unknown, never as zero and never as a percentage.
        totalProfit: k.totalProfit === null || k.totalProfit === undefined ? null : num(k.totalProfit),
        cashSales: num(k.cashSales),
        transferSales: num(k.transferSales),
        creditSales: num(k.creditSales),
    };
    const receipts: ReceiptItem[] = (Array.isArray(data?.receipts) ? data.receipts : []).map((r: any) => ({
        id: r.id,
        bill_no: r.bill_no,
        create_date: r.create_date,
        cashier_name: formatActor(r.cashier_name, UNKNOWN_ACTOR_SHORT),
        member_name: r.member_name || '-',
        member_code: r.member_code || undefined,
        sale_type_name: r.sale_type_name,
        payment_type_name: r.payment_type_name,
        payment_type_id: num(r.payment_type_id),
        total_price: num(r.total_price),
        discount: num(r.discount),
        discount_bill: num(r.discount_bill),
        vat_amount: num(r.vat_amount),
        net_price: num(r.net_price),
        receive: num(r.receive),
        change: num(r.change),
        point: num(r.point),
        remark: r.remark || undefined,
        status: num(r.status),
        item_count: num(r.item_count),
        items: (Array.isArray(r.items) ? r.items : []).map((it: any) => ({
            id: it.id,
            product_id: it.product_id,
            product_code: it.product_code,
            product_name: it.product_name,
            quantity: num(it.quantity),
            price: num(it.price),
            discount: num(it.discount),
            total_price: num(it.total_price),
        })),
    }));
    const dailyTrend = (Array.isArray(data?.dailyTrend) ? data.dailyTrend : []).map((d: any) => ({
        date: formatDateTH(d.date),
        sales: num(d.sales),
        bills: num(d.bills),
    }));
    return { kpis, receipts, dailyTrend };
};

// 2. Best-selling products and category breakdown, from invoice lines.
export const fetchProductReport = async (filter: ReportFilter): Promise<{
    bestSellers: BestSellingProduct[];
    categorySales: CategorySalesSummary[];
}> => {
    const data = await api_report('PRODUCTS', bodyOf(filter));
    const bestSellers: BestSellingProduct[] = (Array.isArray(data?.bestSellers) ? data.bestSellers : []).map((p: any) => ({
        id: p.id,
        product_code: p.product_code,
        product_name: p.product_name,
        category_name: p.category_name,
        unit_name: p.unit_name,
        quantity_sold: num(p.quantity_sold),
        total_sales: num(p.total_sales),
        cost_price: num(p.cost_price),
        profit: num(p.profit),
        profit_margin: num(p.profit_margin),
    }));
    const categorySales: CategorySalesSummary[] = (Array.isArray(data?.categorySales) ? data.categorySales : []).map((c: any) => ({
        category_id: num(c.category_id),
        category_name: c.category_name,
        total_sales: num(c.total_sales),
        total_quantity: num(c.total_quantity),
        percentage: num(c.percentage),
    }));
    return { bestSellers, categorySales };
};

// 3. Stock on hand, valued at recorded product cost.
export const fetchInventoryReport = async (filter?: Partial<ReportFilter>): Promise<{
    stockItems: StockReportItem[];
    totalStockCount: number;
    totalStockCostValue: number;
    lowStockCount: number;
    neverStockedCount: number;
    outOfStockCount: number;
}> => {
    const data = await api_report('INVENTORY', bodyOf(filter));
    const stockItems: StockReportItem[] = (Array.isArray(data?.stockItems) ? data.stockItems : []).map((p: any) => ({
        id: p.id,
        product_code: p.product_code,
        product_name: p.product_name,
        category_name: p.category_name,
        unit_name: p.unit_name,
        quantity: num(p.quantity),
        // Kept null rather than coerced to 0: "not set" is not "zero".
        min_quantity: p.min_quantity === null || p.min_quantity === undefined ? null : num(p.min_quantity),
        cost_price: num(p.cost_price),
        sale_price: num(p.sale_price),
        total_cost_value: num(p.total_cost_value),
        is_low_stock: Boolean(p.is_low_stock),
        is_never_stocked: Boolean(p.is_never_stocked),
        is_out_of_stock: Boolean(p.is_out_of_stock),
    }));
    return {
        stockItems,
        totalStockCount: num(data?.totalStockCount),
        totalStockCostValue: num(data?.totalStockCostValue),
        lowStockCount: num(data?.lowStockCount),
        neverStockedCount: num(data?.neverStockedCount),
        outOfStockCount: num(data?.outOfStockCount),
    };
};

// 4. Work shifts, with the opening / closing employee as recorded.
export const fetchWorkShiftReport = async (filter?: ReportFilter): Promise<ShiftReportItem[]> => {
    const data = await api_report('SHIFTS', bodyOf(filter));
    return (Array.isArray(data) ? data : []).map((s: any) => ({
        id: s.id,
        shift_no: s.shift_no,
        device: s.device || '-',
        cashier_name: formatActor(s.cashier_name, UNKNOWN_ACTOR_SHORT),
        closed_by_name: s.closed_by_name ? formatActor(s.closed_by_name, UNKNOWN_ACTOR_SHORT) : undefined,
        open_date: s.open_date || '-',
        close_date: s.close_date || undefined,
        open_amount: num(s.open_amount),
        close_amount: num(s.close_amount),
        cash_sales: num(s.cash_sales),
        transfer_sales: num(s.transfer_sales),
        credit_sales: num(s.credit_sales),
        total_sales: num(s.total_sales),
        bill_count: num(s.bill_count),
        expected_cash: num(s.expected_cash),
        cash_diff: num(s.cash_diff),
        status: num(s.status),
        entry_breakdown: s.entry_breakdown,
        close_breakdown: s.close_breakdown,
    }));
};

// 5. Members, with spend and visits from their invoices.
export const fetchMemberReport = async (filter?: ReportFilter): Promise<MemberReportItem[]> => {
    const data = await api_report('MEMBERS', bodyOf(filter));
    return (Array.isArray(data) ? data : []).map((mb: any) => ({
        id: mb.id,
        member_code: mb.member_code,
        member_name: mb.member_name,
        phone: mb.phone,
        group_name: mb.group_name,
        total_spent: num(mb.total_spent),
        total_visits: num(mb.total_visits),
        points: num(mb.points),
        last_visit: mb.last_visit,
    }));
};

// 6. Analytics: weekly / monthly bars, payment and category donuts, hourly peaks.
export const fetchAnalyticsReport = async (): Promise<AnalyticsReport> => {
    const data = await api_report('ANALYTICS', {});
    const bars = (rows: any): AnalyticsReport['weeklyData'] =>
        (Array.isArray(rows) ? rows : []).map((d: any) => ({
            label: d.label, value: num(d.value), compareValue: num(d.compareValue), bills: num(d.bills),
        }));
    const donut = (rows: any): AnalyticsReport['paymentDonutData'] =>
        (Array.isArray(rows) ? rows : []).map((d: any) => ({
            label: d.label, value: num(d.value), color: d.color, percentage: num(d.percentage),
        }));
    const k = data?.kpis || {};
    return {
        weeklyData: bars(data?.weeklyData),
        monthlyData: bars(data?.monthlyData),
        paymentDonutData: donut(data?.paymentDonutData),
        categoryDonutData: donut(data?.categoryDonutData),
        hourlyData: (Array.isArray(data?.hourlyData) ? data.hourlyData : []).map((h: any) => ({
            hour: h.hour, sales: num(h.sales), bills: num(h.bills),
        })),
        kpis: {
            totalSales: num(k.totalSales),
            totalPrevSales: num(k.totalPrevSales),
            totalBills: num(k.totalBills),
            growthPercent: num(k.growthPercent),
            avgPerBill: num(k.avgPerBill),
        },
    };
};
